import { Archive, CheckCircle } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { gmailArchiveEmail } from 'sprites-types/workflow/tools/gmailArchiveEmail'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GmailArchiveEmailDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(gmailArchiveEmail, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const messageId = inputs.messageId || inputs.id || 'email'
  const emailCount = Array.isArray(result) ? result.length : 1

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="flex items-center gap-4 px-4 py-3">
        <div className="flex-shrink-0">
          <div className="rounded-full bg-green-100 p-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-gray-900">
            Email{emailCount > 1 ? 's' : ''} archived successfully
          </div>
          <div className="text-xs text-gray-600">
            {emailCount > 1 ? `${emailCount} emails` : `Message ID: ${messageId}`} moved to archive
          </div>
        </div>
        <div className="flex-shrink-0">
          <Archive className="h-5 w-5 text-gray-400" />
        </div>
      </div>
    </div>
  )
}

function downloadGmailArchiveEmail(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function gmailArchiveEmailToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(gmailArchiveEmail, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const emailCount = Array.isArray(result) ? result.length : 1

  return `## Email${emailCount > 1 ? 's' : ''} Archived Successfully

${emailCount > 1 ? `${emailCount} emails` : 'Email'} moved to archive.

---
`
}

export const GmailArchiveEmailDisplay = createWorkflowToolComponent(
  GmailArchiveEmailDisplayComponent,
  downloadGmailArchiveEmail,
  gmailArchiveEmailToMarkdown
)
